'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  X, Navigation, MapPin, Clock, Route, Truck, Wrench, User,
  Wifi, WifiOff, AlertTriangle, Crosshair, ExternalLink, RefreshCw
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import type { Asset } from './LiveTrackingView';

interface AssetMapModalProps {
  asset: Asset;
  onClose: () => void;
}

interface GeoPosition {
  lat: number;
  lng: number;
  accuracy: number;
  timestamp: number;
}

const typeIcon = {
  vehicle: Truck,
  equipment: Wrench,
  person: User,
};

const statusColor: Record<string, string> = {
  'en-route': '#38BDF8',
  'on-site': '#A78BFA',
  'returning': '#F59E0B',
  'available': '#34D399',
  'offline': '#71717A',
  'maintenance': '#F97316',
  'dispatched': '#60A5FA',
};

// Convert lat/lng to SVG canvas coordinates (Permian Basin region approx)
function latLngToCanvas(lat: number, lng: number, width: number, height: number) {
  // Permian Basin bounding box approx: lat 31.4–32.2, lng -103.4 to -101.8
  const minLat = 31.4, maxLat = 32.2;
  const minLng = -103.4, maxLng = -101.8;
  const x = ((lng - minLng) / (maxLng - minLng)) * width;
  const y = ((maxLat - lat) / (maxLat - minLat)) * height;
  return { x, y };
}

export default function AssetMapModal({ asset, onClose }: AssetMapModalProps) {
  const [geoPosition, setGeoPosition] = useState<GeoPosition | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoWatchId, setGeoWatchId] = useState<number | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const [svgSize, setSvgSize] = useState({ width: 600, height: 400 });

  const AssetIcon = typeIcon[asset.type];
  const assetColor = statusColor[asset.status] ?? '#71717A';

  // Measure SVG container
  useEffect(() => {
    const el = svgRef.current?.parentElement;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSvgSize({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    ro.observe(el);
    setSvgSize({ width: el.clientWidth, height: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  // Start GPS tracking
  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by this browser.');
      return;
    }
    setGeoLoading(true);
    setGeoError(null);
    setIsTracking(true);

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setGeoPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: pos.timestamp,
        });
        setGeoLoading(false);
      },
      (err) => {
        setGeoLoading(false);
        setIsTracking(false);
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setGeoError('Location access denied. Please allow location in your browser settings.');
            break;
          case err.POSITION_UNAVAILABLE:
            setGeoError('Location unavailable. Check your device GPS settings.');
            break;
          case err.TIMEOUT:
            setGeoError('Location request timed out. Try again.');
            break;
          default:
            setGeoError('Unable to retrieve location.');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );
    setGeoWatchId(watchId);
  }, []);

  // Stop GPS tracking
  const stopTracking = useCallback(() => {
    if (geoWatchId !== null) {
      navigator.geolocation.clearWatch(geoWatchId);
      setGeoWatchId(null);
    }
    setIsTracking(false);
  }, [geoWatchId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (geoWatchId !== null) {
        navigator.geolocation.clearWatch(geoWatchId);
      }
    };
  }, [geoWatchId]);

  // Open Google Maps with route from current location (or asset location) to job site
  const openDirections = () => {
    const origin = geoPosition
      ? `${geoPosition.lat},${geoPosition.lng}`
      : `${asset.lat},${asset.lng}`;
    // Use job site as destination — encode for URL
    const destination = asset.jobSite
      ? encodeURIComponent(asset.jobSite)
      : `${asset.lat + 0.15},${asset.lng + 0.15}`;
    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    window.open(url, '_blank');
  };

  // Compute canvas positions
  const { width: W, height: H } = svgSize;
  const assetPos = latLngToCanvas(asset.lat, asset.lng, W, H);
  // Simulated job destination (offset from asset)
  const jobLat = asset.lat + 0.18;
  const jobLng = asset.lng + 0.22;
  const jobPos = latLngToCanvas(jobLat, jobLng, W, H);
  const myPos = geoPosition ? latLngToCanvas(geoPosition.lat, geoPosition.lng, W, H) : null;

  const routeProgress = asset.routeProgress ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center border"
              style={{ backgroundColor: `${assetColor}22`, borderColor: `${assetColor}44` }}
            >
              <AssetIcon size={15} style={{ color: assetColor }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-bold text-amber-400">{asset.unitNumber}</span>
                {asset.signal === 'lost'
                  ? <WifiOff size={12} className="text-red-400" />
                  : asset.signal === 'weak'
                  ? <Wifi size={12} className="text-amber-400" />
                  : <Wifi size={12} className="text-emerald-400" />
                }
                <StatusBadge status={asset.status as any} size="sm" />
              </div>
              {asset.driver && <p className="text-xs text-zinc-400">{asset.driver}</p>}
              {!asset.driver && <p className="text-xs text-zinc-400">{asset.make} {asset.model}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 transition-all"
          >
            <X size={16} />
          </button>
        </div>

        {/* Map area */}
        <div className="relative flex-1 bg-zinc-950 overflow-hidden" style={{ minHeight: 280 }}>
          <svg ref={svgRef} width="100%" height="100%" className="absolute inset-0">
            {/* Grid */}
            <defs>
              <pattern id="modal-grid" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgb(39 39 42)" strokeWidth="0.5" />
              </pattern>
              <pattern id="modal-grid-major" width="150" height="150" patternUnits="userSpaceOnUse">
                <path d="M 150 0 L 0 0 0 150" fill="none" stroke="rgb(52 52 58)" strokeWidth="1" />
              </pattern>
              <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                <polygon points="0 0, 8 3, 0 6" fill="#F59E0B" opacity="0.8" />
              </marker>
            </defs>
            <rect width="100%" height="100%" fill="url(#modal-grid)" />
            <rect width="100%" height="100%" fill="url(#modal-grid-major)" />

            {/* Simulated roads */}
            <line x1="0" y1={H * 0.45} x2={W} y2={H * 0.48} stroke="rgb(63 63 70)" strokeWidth="3" />
            <line x1={W * 0.3} y1="0" x2={W * 0.35} y2={H} stroke="rgb(63 63 70)" strokeWidth="2" />
            <line x1={W * 0.6} y1="0" x2={W * 0.65} y2={H} stroke="rgb(63 63 70)" strokeWidth="2.5" />
            <path d={`M ${W * 0.2} 0 Q ${W * 0.45} ${H * 0.3} ${W * 0.8} ${H * 0.6}`} fill="none" stroke="rgb(63 63 70)" strokeWidth="2" />

            {/* Route line: asset → job destination */}
            {asset.jobSite && (
              <line
                x1={assetPos.x} y1={assetPos.y}
                x2={jobPos.x} y2={jobPos.y}
                stroke="#F59E0B"
                strokeWidth="2.5"
                strokeDasharray="8 5"
                opacity="0.7"
                markerEnd="url(#arrowhead)"
              />
            )}

            {/* Route line: my GPS → asset (if tracking) */}
            {myPos && (
              <line
                x1={myPos.x} y1={myPos.y}
                x2={assetPos.x} y2={assetPos.y}
                stroke="#38BDF8"
                strokeWidth="2"
                strokeDasharray="6 4"
                opacity="0.6"
              />
            )}

            {/* Job destination pin */}
            {asset.jobSite && (
              <g transform={`translate(${jobPos.x}, ${jobPos.y})`}>
                <circle r="14" fill="#A78BFA22" stroke="#A78BFA" strokeWidth="1.5" />
                <text textAnchor="middle" dominantBaseline="central" fontSize="12" fill="#A78BFA">📍</text>
                <text x="0" y="22" textAnchor="middle" fontSize="9" fill="#A78BFA" fontFamily="JetBrains Mono">
                  {asset.jobSite?.split(',')[0]}
                </text>
              </g>
            )}

            {/* Asset pin */}
            <g transform={`translate(${assetPos.x}, ${assetPos.y})`}>
              <circle r="18" fill={`${assetColor}22`} stroke={assetColor} strokeWidth="2">
                <animate attributeName="r" values="18;22;18" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle r="12" fill={`${assetColor}33`} stroke={assetColor} strokeWidth="1.5" />
              <text textAnchor="middle" dominantBaseline="central" fontSize="11" fill={assetColor} fontWeight="bold">
                {asset.type === 'vehicle' ? '🚛' : asset.type === 'equipment' ? '🔧' : '👤'}
              </text>
              <text x="0" y="26" textAnchor="middle" fontSize="9" fill={assetColor} fontFamily="JetBrains Mono" fontWeight="bold">
                {asset.unitNumber}
              </text>
            </g>

            {/* My GPS location pin */}
            {myPos && (
              <g transform={`translate(${myPos.x}, ${myPos.y})`}>
                <circle r="10" fill="#38BDF822" stroke="#38BDF8" strokeWidth="2" />
                <circle r="4" fill="#38BDF8" />
                <circle r="16" fill="none" stroke="#38BDF8" strokeWidth="1" opacity="0.4">
                  <animate attributeName="r" values="10;20;10" dur="1.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.4;0;0.4" dur="1.5s" repeatCount="indefinite" />
                </circle>
                <text x="0" y="24" textAnchor="middle" fontSize="9" fill="#38BDF8" fontFamily="JetBrains Mono">You</text>
              </g>
            )}

            {/* Region labels */}
            <text x={W * 0.72} y={H * 0.12} fill="rgb(82 82 91)" fontSize="10" fontFamily="JetBrains Mono">Midland, TX</text>
            <text x={W * 0.25} y={H * 0.58} fill="rgb(82 82 91)" fontSize="10" fontFamily="JetBrains Mono">Odessa, TX</text>
            <text x={W * 0.08} y={H * 0.88} fill="rgb(82 82 91)" fontSize="10" fontFamily="JetBrains Mono">Loving County</text>
          </svg>

          {/* GPS status overlay */}
          <div className="absolute top-3 left-3 z-10">
            {!isTracking && !geoPosition && (
              <button
                onClick={startTracking}
                disabled={geoLoading}
                className="flex items-center gap-2 px-3 py-2 bg-zinc-900/95 border border-zinc-700 hover:border-amber-500/50 rounded-xl text-xs font-semibold text-zinc-300 hover:text-amber-400 transition-all shadow-lg backdrop-blur-sm disabled:opacity-60"
              >
                <Crosshair size={13} className={geoLoading ? 'animate-spin' : ''} />
                {geoLoading ? 'Locating...' : 'Activate My GPS'}
              </button>
            )}
            {isTracking && geoPosition && (
              <div className="flex items-center gap-2 px-3 py-2 bg-zinc-900/95 border border-emerald-500/30 rounded-xl shadow-lg backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
                <span className="text-xs text-emerald-400 font-semibold">GPS Active</span>
                <span className="text-[10px] text-zinc-500 font-mono">±{Math.round(geoPosition.accuracy)}m</span>
                <button onClick={stopTracking} className="ml-1 text-zinc-600 hover:text-zinc-400 transition-colors">
                  <X size={11} />
                </button>
              </div>
            )}
            {geoError && (
              <div className="flex items-start gap-2 px-3 py-2 bg-red-950/80 border border-red-500/30 rounded-xl max-w-xs shadow-lg backdrop-blur-sm">
                <AlertTriangle size={12} className="text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[11px] text-red-300">{geoError}</p>
                  <button onClick={startTracking} className="text-[10px] text-red-400 hover:text-red-300 mt-1 flex items-center gap-1">
                    <RefreshCw size={9} /> Retry
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Map legend */}
          <div className="absolute bottom-3 right-3 z-10 bg-zinc-900/90 border border-zinc-700 rounded-lg px-3 py-2 backdrop-blur-sm text-[10px] space-y-1">
            <div className="flex items-center gap-1.5">
              <span className="w-4 border-t-2 border-dashed border-amber-500 opacity-70" />
              <span className="text-zinc-400">Route to job</span>
            </div>
            {geoPosition && (
              <div className="flex items-center gap-1.5">
                <span className="w-4 border-t-2 border-dashed border-sky-400 opacity-60" />
                <span className="text-zinc-400">Your location</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: assetColor }} />
              <span className="text-zinc-400">Asset</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0" />
              <span className="text-zinc-400">Destination</span>
            </div>
          </div>
        </div>

        {/* Info bar */}
        <div className="flex-shrink-0 border-t border-zinc-800 px-5 py-3 flex flex-wrap items-center gap-4">
          {/* Asset GPS coords */}
          <div className="flex items-center gap-1.5 text-[11px] text-zinc-500 font-mono">
            <Navigation size={11} className="text-zinc-600" />
            <span>{asset.lat.toFixed(4)}°, {asset.lng.toFixed(4)}°</span>
            {asset.speed > 0 && <span className="text-zinc-600">· {asset.speed} mph {asset.heading}</span>}
          </div>

          {/* Job info */}
          {asset.jobSite && (
            <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
              <MapPin size={11} className="text-purple-400" />
              <span className="text-zinc-400">{asset.jobSite}</span>
            </div>
          )}

          {/* ETA */}
          {asset.etaMinutes !== undefined && asset.etaMinutes > 0 && (
            <div className="flex items-center gap-1.5 text-[11px]">
              <Clock size={11} className="text-zinc-600" />
              <span className="text-zinc-500">ETA:</span>
              <span className={`font-mono font-semibold ${asset.etaMinutes > 15 ? 'text-red-400' : 'text-emerald-400'}`}>
                {asset.etaMinutes} min
              </span>
            </div>
          )}

          {/* Route progress */}
          {routeProgress > 0 && (
            <div className="flex items-center gap-2 text-[11px]">
              <Route size={11} className="text-zinc-600" />
              <div className="flex items-center gap-1.5">
                <div className="w-20 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${routeProgress}%`,
                      backgroundColor: routeProgress >= 90 ? '#34D399' : routeProgress >= 50 ? '#F59E0B' : '#38BDF8',
                    }}
                  />
                </div>
                <span className="font-mono text-zinc-400">{routeProgress}%</span>
              </div>
            </div>
          )}

          {/* My GPS coords */}
          {geoPosition && (
            <div className="flex items-center gap-1.5 text-[11px] text-sky-400 font-mono ml-auto">
              <Crosshair size={11} />
              <span>You: {geoPosition.lat.toFixed(4)}°, {geoPosition.lng.toFixed(4)}°</span>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex-shrink-0 px-5 py-3 border-t border-zinc-800 flex flex-wrap gap-2">
          {asset.jobSite && (
            <button
              onClick={openDirections}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-semibold rounded-lg transition-all duration-150 active:scale-95"
            >
              <Navigation size={13} />
              Get Directions to Job
              <ExternalLink size={11} />
            </button>
          )}
          {!isTracking ? (
            <button
              onClick={startTracking}
              disabled={geoLoading}
              className="flex items-center gap-2 px-4 py-2 bg-sky-500/20 hover:bg-sky-500/30 border border-sky-500/30 text-sky-400 text-xs font-semibold rounded-lg transition-all duration-150 active:scale-95 disabled:opacity-60"
            >
              <Crosshair size={13} className={geoLoading ? 'animate-spin' : ''} />
              {geoLoading ? 'Locating...' : 'Track My Location'}
            </button>
          ) : (
            <button
              onClick={stopTracking}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 text-xs font-semibold rounded-lg transition-all duration-150 active:scale-95"
            >
              <X size={13} />
              Stop Tracking
            </button>
          )}
          <button
            onClick={onClose}
            className="ml-auto px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 text-xs font-semibold rounded-lg transition-all duration-150 active:scale-95"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
